/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useEffect, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DataLicensingRedistributionModels.module.scss';

type Translate = (key: string) => string;

type Model = {
  num: string;
  name: string;
  tier: number;
  accent: string;
  reaches: string;
  may: string;
};

const getModels = (t: Translate): Model[] => [
  {
    num: '01',
    name: t('Internal use'),
    tier: 1,
    accent: '#9AA6D6',
    reaches: t('Your own team and systems'),
    may: t('Enrich internal systems'),
  },
  {
    num: '02',
    name: t('Embedded'),
    tier: 2,
    accent: '#7E9BFF',
    reaches: t('Your users, on screen'),
    may: t('Display inside your product'),
  },
  {
    num: '03',
    name: t('Redistribution'),
    tier: 3,
    accent: '#5265F0',
    reaches: t('Your users, in files they keep'),
    may: t('Export, download and retain'),
  },
  {
    num: '04',
    name: t('AI & model development'),
    tier: 4,
    accent: '#3642F5',
    reaches: t('Your models and retrieval indexes'),
    may: t('Train, fine-tune, evaluate, ground'),
  },
];

export const DataLicensingRedistributionModels = () => {
  const { t } = useInt();
  const models = getModels(t);
  const headRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    const revealables = [headRef.current, ...cardRefs.current].filter(
      (el): el is HTMLDivElement | HTMLLIElement => Boolean(el),
    );

    if (!revealables.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealed);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 },
    );

    revealables.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <Section className={styles.root}>
      <div ref={headRef} className={cn(styles.head, styles.reveal)}>
        <TitleBlock
          align="left"
          title={t('Four ways teams license it')}
          description={t(
            'The difference between the four is who the data reaches and what they may do once it gets there. The grant scales with what you are building.',
          )}
        />
      </div>

      <ul className={styles.track}>
        {models.map((model, i) => (
          <li
            key={model.num}
            ref={(el) => {
              cardRefs.current[i] = el;
            }}
            className={cn(styles.card, styles.reveal)}
            style={{ '--accent': model.accent, transitionDelay: `${i * 90}ms` } as CSSProperties}
          >
            <span className={styles.cardGlow} aria-hidden="true" />

            <div className={styles.cardHead}>
              <em className={styles.num}>{model.num}</em>
              <span className={styles.name}>{model.name}</span>
            </div>

            <div className={styles.tierRow}>
              <span className={styles.tier} aria-hidden="true">
                {[1, 2, 3, 4].map((step) => (
                  <i key={step} className={cn(styles.tierBar, step <= model.tier && styles.tierBarFilled)} />
                ))}
              </span>
              <span className={styles.tierLabel}>
                {model.tier} {t('of 4')}
              </span>
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t('Reaches')}</span>
              <span className={styles.fieldValue}>{model.reaches}</span>
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t('May')}</span>
              <span className={styles.mayBadge}>{model.may}</span>
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
};
